import { useState, useEffect, useCallback } from 'react'
import { type LancamentoFinanceiro } from '@services/db.js'
import {
  getLancamentos,
  addLancamento,
  updateLancamento,
  deleteLancamento,
  getObraConfig,
  saveOrcamentoCategorias as dbSaveOrcamento,
} from '@services/crud.js'

export type { LancamentoFinanceiro }

export interface LancamentosData {
  lancamentos: LancamentoFinanceiro[]
  orcamentoTotal: number
  orcamentoPorCategoria: Record<string, number>
  totalGasto: number
  totalReceitas: number
  saldo: number
  gastoPorCategoria: Record<string, number>
  isLoading: boolean
}

const INITIAL: LancamentosData = {
  lancamentos: [],
  orcamentoTotal: 0,
  orcamentoPorCategoria: {},
  totalGasto: 0,
  totalReceitas: 0,
  saldo: 0,
  gastoPorCategoria: {},
  isLoading: true,
}

export function useLancamentos() {
  const [data, setData] = useState<LancamentosData>(INITIAL)

  const load = useCallback(async () => {
    const [all, config] = await Promise.all([getLancamentos(), getObraConfig()])

    const totalGasto = all
      .filter((l) => l.status === 'pago' && l.tipo !== 'receita')
      .reduce((s, l) => s + l.valor, 0)
    const totalReceitas = all.filter((l) => l.tipo === 'receita').reduce((s, l) => s + l.valor, 0)
    const orcamentoTotal = config?.orcamentoTotal ?? 0
    const saldo = orcamentoTotal - totalGasto + totalReceitas

    const gastoPorCategoria: Record<string, number> = {}
    for (const l of all) {
      gastoPorCategoria[l.categoria] = (gastoPorCategoria[l.categoria] ?? 0) + l.valor
    }

    setData({
      lancamentos: all,
      orcamentoTotal,
      orcamentoPorCategoria: config?.orcamentoPorCategoria ?? {},
      totalGasto,
      totalReceitas,
      saldo,
      gastoPorCategoria,
      isLoading: false,
    })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const add = useCallback(
    async (payload: Omit<LancamentoFinanceiro, 'id' | 'criadoEm' | 'atualizadoEm'>) => {
      await addLancamento(payload)
      await load()
    },
    [load],
  )

  const update = useCallback(
    async (id: number, patch: Partial<Omit<LancamentoFinanceiro, 'id' | 'criadoEm'>>) => {
      await updateLancamento(id, patch)
      await load()
    },
    [load],
  )

  const remove = useCallback(
    async (id: number) => {
      await deleteLancamento(id)
      await load()
    },
    [load],
  )

  const saveOrcamento = useCallback(
    async (orcamento: Record<string, number>) => {
      await dbSaveOrcamento(orcamento)
      await load()
    },
    [load],
  )

  return { ...data, reload: load, add, update, remove, saveOrcamento }
}
